import React, { useState, useRef, useEffect } from 'react';
import { LayoutGrid } from 'lucide-react';

const DEFAULT_COLORS = ['#4f46e5','#0891b2','#059669','#d97706','#dc2626','#7c3aed','#db2777','#475569'];

function pickColor(name, color) {
    if (color) return color;
    // Deterministic color from name
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return DEFAULT_COLORS[Math.abs(hash) % DEFAULT_COLORS.length];
}

function AppIcon({ name, iconUrl, color }) {
    const [imgError, setImgError] = useState(false);
    const bg = pickColor(name, color);
    const letter = (name || '?').charAt(0).toUpperCase();

    if (iconUrl && !imgError) {
        return (
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-stone-100 flex items-center justify-center">
                <img
                    src={iconUrl}
                    alt={name}
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                />
            </div>
        );
    }

    return (
        <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-base"
            style={{ backgroundColor: bg }}
        >
            {letter}
        </div>
    );
}

export default function WaffleMenu({ appLinks = [] }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Click outside to close
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    if (!appLinks || appLinks.length === 0) return null;

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className="relative p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors"
                title="App Launcher"
            >
                <LayoutGrid className="w-5 h-5" />
            </button>

            {open && (
                <div className="absolute right-0 top-12 w-72 bg-white rounded-xl border border-stone-200 shadow-xl z-50 animate-fade-in">
                    <div className="px-4 py-3 border-b border-stone-200">
                        <h3 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Apps
                        </h3>
                    </div>
                    <div className="p-3 grid grid-cols-3 gap-1">
                        {[...appLinks]
                            .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
                            .map((link) => (
                                <a
                                    key={link.id || link.name}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setOpen(false)}
                                    className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg hover:bg-stone-50 transition-colors group"
                                >
                                    <AppIcon name={link.name} iconUrl={link.icon_url} color={link.color} />
                                    <span className="text-[11px] text-stone-600 group-hover:text-stone-900 font-medium text-center leading-tight truncate w-full">
                                        {link.name}
                                    </span>
                                </a>
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
}
